"use client";

import type { CSSProperties, DependencyList, HTMLAttributes, Key } from "react";
import * as React from "react";
import { cn } from "../../lib";
import {
	CONTENT_TOOLTIP_SHORT,
	ContentTooltip,
	type ContentTooltipSide,
} from "../design-system/tooltip";

type LogoTooltipOptional = {
	/** When set, wraps the logo trigger with Prefer-over ContentTooltip. */
	tooltipContent?: React.ReactNode;
	tooltipSide?: ContentTooltipSide;
	tooltipContentClassName?: string;
};

export type LogoItem =
	| ({
			node: React.ReactNode;
			href?: string;
			title?: string;
			ariaLabel?: string;
	  } & LogoTooltipOptional)
	| ({
			src: string;
			alt?: string;
			href?: string;
			title?: string;
			srcSet?: string;
			sizes?: string;
			width?: number;
			height?: number;
	  } & LogoTooltipOptional);

function wrapWithTooltip(
	item: LogoItem,
	inner: React.ReactNode,
): React.ReactNode {
	const tooltipContent =
		"tooltipContent" in item ? item.tooltipContent : undefined;
	if (tooltipContent == null) {
		return inner;
	}
	const side =
		"tooltipSide" in item && item.tooltipSide !== undefined
			? item.tooltipSide
			: "top";
	const tooltipContentClassName =
		"tooltipContentClassName" in item
			? item.tooltipContentClassName
			: undefined;
	return (
		<ContentTooltip
			{...CONTENT_TOOLTIP_SHORT}
			content={tooltipContent}
			side={side}
			contentClassName={tooltipContentClassName}
		>
			{React.isValidElement(inner) ? inner : <span>{inner}</span>}
		</ContentTooltip>
	);
}

export interface LogoLoopProps
	extends Omit<HTMLAttributes<HTMLElement>, "children"> {
	logos: LogoItem[];
	speed?: number;
	direction?: "left" | "right" | "up" | "down";
	width?: number | string;
	logoHeight?: number;
	gap?: number;
	pauseOnHover?: boolean;
	hoverSpeed?: number;
	fadeOut?: boolean;
	fadeOutColor?: string;
	scaleOnHover?: boolean;
	/** Extra classes on `<img>` for image logos (e.g. dark-mode invert). */
	logoImgClassName?: string;
	renderItem?: (item: LogoItem, key: Key) => React.ReactNode;
	ariaLabel?: string;
	/** Reserved for API compatibility with examples; currently unused. */
	useCustomRender?: boolean;
}

const ANIMATION_CONFIG = {
	SMOOTH_TAU: 0.25,
	MIN_COPIES: 2,
	COPY_HEADROOM: 2,
} as const;

function toCssLength(value?: number | string): string | undefined {
	return typeof value === "number" ? `${value}px` : (value ?? undefined);
}

function useResizeObserver(
	callback: () => void,
	elements: Array<React.RefObject<Element | null>>,
	dependencies: DependencyList,
): void {
	React.useEffect(() => {
		if (typeof window === "undefined") {
			return;
		}
		if (!window.ResizeObserver) {
			const handleResize = () => callback();
			window.addEventListener("resize", handleResize);
			callback();
			return () => window.removeEventListener("resize", handleResize);
		}

		const observers = elements.map((ref) => {
			if (!ref.current) {
				return null;
			}
			const observer = new ResizeObserver(callback);
			observer.observe(ref.current);
			return observer;
		});

		callback();

		return () => {
			for (const observer of observers) {
				observer?.disconnect();
			}
		};
	}, dependencies);
}

function useImageLoader(
	seqRef: React.RefObject<HTMLUListElement | null>,
	onLoad: () => void,
	dependencies: DependencyList,
): void {
	React.useEffect(() => {
		const images = seqRef.current?.querySelectorAll("img") ?? [];

		if (images.length === 0) {
			onLoad();
			return;
		}

		let remainingImages = images.length;
		const handleImageLoad = () => {
			remainingImages -= 1;
			if (remainingImages === 0) {
				onLoad();
			}
		};

		for (const img of images) {
			const htmlImg = img as HTMLImageElement;
			if (htmlImg.complete) {
				handleImageLoad();
			} else {
				htmlImg.addEventListener("load", handleImageLoad, {
					once: true,
				});
				htmlImg.addEventListener("error", handleImageLoad, {
					once: true,
				});
			}
		}

		return () => {
			for (const img of images) {
				img.removeEventListener("load", handleImageLoad);
				img.removeEventListener("error", handleImageLoad);
			}
		};
	}, dependencies);
}

function useAnimationLoop(
	trackRef: React.RefObject<HTMLDivElement | null>,
	targetVelocity: number,
	seqWidth: number,
	seqHeight: number,
	isHovered: boolean,
	hoverSpeed: number | undefined,
	isVertical: boolean,
): void {
	const rafRef = React.useRef<number | null>(null);
	const lastTimestampRef = React.useRef<number | null>(null);
	const offsetRef = React.useRef(0);
	const velocityRef = React.useRef(0);

	React.useEffect(() => {
		const track = trackRef.current;
		if (!track) {
			return;
		}

		const prefersReduced =
			typeof globalThis.window !== "undefined" &&
			globalThis.window.matchMedia?.("(prefers-reduced-motion: reduce)")
				.matches;

		const seqSize = isVertical ? seqHeight : seqWidth;

		if (seqSize > 0) {
			offsetRef.current =
				((offsetRef.current % seqSize) + seqSize) % seqSize;
			const transformValue = isVertical
				? `translate3d(0, ${-offsetRef.current}px, 0)`
				: `translate3d(${-offsetRef.current}px, 0, 0)`;
			track.style.transform = transformValue;
		}

		if (prefersReduced) {
			track.style.transform = isVertical
				? "translate3d(0, 0, 0)"
				: "translate3d(0, 0, 0)";
			return () => {
				lastTimestampRef.current = null;
			};
		}

		/**
		 * Until the sequence measures a positive size (ResizeObserver / layout),
		 * do not schedule RAF — otherwise we burn a frame loop forever doing
		 * useless easing work (waitlist LogoLoop hit this inside fade-in wrappers).
		 */
		if (seqSize <= 0) {
			return () => {
				lastTimestampRef.current = null;
			};
		}

		const animate = (timestamp: number) => {
			if (lastTimestampRef.current === null) {
				lastTimestampRef.current = timestamp;
			}

			const deltaTime =
				Math.max(0, timestamp - lastTimestampRef.current) / 1000;
			lastTimestampRef.current = timestamp;

			const target =
				isHovered && hoverSpeed !== undefined
					? hoverSpeed
					: targetVelocity;

			const easingFactor =
				1 - Math.exp(-deltaTime / ANIMATION_CONFIG.SMOOTH_TAU);
			velocityRef.current +=
				(target - velocityRef.current) * easingFactor;

			if (seqSize > 0) {
				let nextOffset =
					offsetRef.current + velocityRef.current * deltaTime;
				nextOffset = ((nextOffset % seqSize) + seqSize) % seqSize;
				offsetRef.current = nextOffset;

				const transformValue = isVertical
					? `translate3d(0, ${-offsetRef.current}px, 0)`
					: `translate3d(${-offsetRef.current}px, 0, 0)`;
				track.style.transform = transformValue;
			}

			rafRef.current = requestAnimationFrame(animate);
		};

		rafRef.current = requestAnimationFrame(animate);

		return () => {
			if (rafRef.current !== null) {
				cancelAnimationFrame(rafRef.current);
				rafRef.current = null;
			}
			lastTimestampRef.current = null;
		};
	}, [
		targetVelocity,
		seqWidth,
		seqHeight,
		isHovered,
		hoverSpeed,
		isVertical,
	]);
}

function isNodeLogoItem(
	item: LogoItem,
): item is Extract<LogoItem, { node: React.ReactNode }> {
	return "node" in item;
}

function LogoLoopInner({
	logos,
	speed = 120,
	direction = "left",
	width = "100%",
	logoHeight = 28,
	gap = 32,
	pauseOnHover,
	hoverSpeed,
	fadeOut = false,
	fadeOutColor,
	scaleOnHover = false,
	logoImgClassName,
	renderItem,
	ariaLabel = "Partner logos",
	className,
	style,
	useCustomRender: _useCustomRender,
	...rest
}: LogoLoopProps) {
	void _useCustomRender;

	const containerRef = React.useRef<HTMLElement | null>(null);
	const trackRef = React.useRef<HTMLDivElement>(null);
	const seqRef = React.useRef<HTMLUListElement>(null);

	const [seqWidth, setSeqWidth] = React.useState<number>(0);
	const [seqHeight, setSeqHeight] = React.useState<number>(0);
	const [copyCount, setCopyCount] = React.useState<number>(
		ANIMATION_CONFIG.MIN_COPIES,
	);
	const [isHovered, setIsHovered] = React.useState<boolean>(false);

	const effectiveHoverSpeed = React.useMemo(() => {
		if (hoverSpeed !== undefined) {
			return hoverSpeed;
		}
		if (pauseOnHover === true) {
			return 0;
		}
		if (pauseOnHover === false) {
			return undefined;
		}
		return 0;
	}, [hoverSpeed, pauseOnHover]);

	const isVertical = direction === "up" || direction === "down";

	const targetVelocity = React.useMemo(() => {
		const magnitude = Math.abs(speed);
		let directionMultiplier: number;
		if (isVertical) {
			directionMultiplier = direction === "up" ? 1 : -1;
		} else {
			directionMultiplier = direction === "left" ? 1 : -1;
		}
		const speedMultiplier = speed < 0 ? -1 : 1;
		return magnitude * directionMultiplier * speedMultiplier;
	}, [speed, direction, isVertical]);

	const updateDimensions = React.useCallback(() => {
		const containerWidth = containerRef.current?.clientWidth ?? 0;
		const sequenceRect = seqRef.current?.getBoundingClientRect?.();
		const sequenceWidth = sequenceRect?.width ?? 0;
		const sequenceHeight = sequenceRect?.height ?? 0;
		if (isVertical) {
			const parentHeight =
				containerRef.current?.parentElement?.clientHeight ?? 0;
			if (containerRef.current && parentHeight > 0) {
				const targetHeight = Math.ceil(parentHeight);
				if (containerRef.current.style.height !== `${targetHeight}px`) {
					containerRef.current.style.height = `${targetHeight}px`;
				}
			}
			if (sequenceHeight > 0) {
				setSeqHeight(Math.ceil(sequenceHeight));
				const viewport =
					containerRef.current?.clientHeight ??
					parentHeight ??
					sequenceHeight;
				const copiesNeeded =
					Math.ceil(viewport / sequenceHeight) +
					ANIMATION_CONFIG.COPY_HEADROOM;
				setCopyCount(
					Math.max(ANIMATION_CONFIG.MIN_COPIES, copiesNeeded),
				);
			}
		} else if (sequenceWidth > 0) {
			setSeqWidth(Math.ceil(sequenceWidth));
			const copiesNeeded =
				Math.ceil(containerWidth / sequenceWidth) +
				ANIMATION_CONFIG.COPY_HEADROOM;
			setCopyCount(Math.max(ANIMATION_CONFIG.MIN_COPIES, copiesNeeded));
		}
	}, [isVertical]);

	useResizeObserver(
		updateDimensions,
		[containerRef, seqRef],
		[logos, gap, logoHeight, isVertical],
	);

	useImageLoader(seqRef, updateDimensions, [
		logos,
		gap,
		logoHeight,
		isVertical,
	]);

	useAnimationLoop(
		trackRef,
		targetVelocity,
		seqWidth,
		seqHeight,
		isHovered,
		effectiveHoverSpeed,
		isVertical,
	);

	const cssVariables = React.useMemo(
		() =>
			({
				"--logoloop-gap": `${gap}px`,
				"--logoloop-logoHeight": `${logoHeight}px`,
				...(fadeOutColor
					? { "--logoloop-fadeColor": fadeOutColor }
					: {}),
			}) as CSSProperties,
		[gap, logoHeight, fadeOutColor],
	);

	const rootClasses = React.useMemo(
		() =>
			cn(
				"group relative",
				isVertical
					? "inline-block h-full overflow-hidden"
					: "overflow-x-hidden",
				"[--logoloop-gap:32px]",
				"[--logoloop-logoHeight:28px]",
				"[--logoloop-fadeColorAuto:#ffffff]",
				"dark:[--logoloop-fadeColorAuto:#0b0b0b]",
				scaleOnHover && "py-[calc(var(--logoloop-logoHeight)*0.1)]",
				className,
			),
		[isVertical, scaleOnHover, className],
	);

	const handleMouseEnter = React.useCallback(() => {
		if (effectiveHoverSpeed !== undefined) {
			setIsHovered(true);
		}
	}, [effectiveHoverSpeed]);

	const handleMouseLeave = React.useCallback(() => {
		if (effectiveHoverSpeed !== undefined) {
			setIsHovered(false);
		}
	}, [effectiveHoverSpeed]);

	const renderLogoItem = React.useCallback(
		(item: LogoItem, key: Key) => {
			if (renderItem) {
				const rendered = renderItem(item, key);
				const tooltipContent =
					"tooltipContent" in item ? item.tooltipContent : undefined;
				const wrapped =
					tooltipContent != null ? (
						<ContentTooltip
							{...CONTENT_TOOLTIP_SHORT}
							content={tooltipContent}
							side={
								"tooltipSide" in item &&
								item.tooltipSide !== undefined
									? item.tooltipSide
									: "top"
							}
							contentClassName={
								"tooltipContentClassName" in item
									? item.tooltipContentClassName
									: undefined
							}
						>
							<span className="inline-flex">{rendered}</span>
						</ContentTooltip>
					) : (
						rendered
					);
				return (
					<li
						className={cn(
							"flex-none text-[length:var(--logoloop-logoHeight)] leading-[1]",
							isVertical
								? "mb-[var(--logoloop-gap)]"
								: "mr-[var(--logoloop-gap)]",
							scaleOnHover && "group/item overflow-visible",
						)}
						key={key}
					>
						{wrapped}
					</li>
				);
			}

			const isNodeItem = isNodeLogoItem(item);

			const content = isNodeItem ? (
				<span
					className={cn(
						"inline-flex items-center",
						"motion-reduce:transition-none",
						scaleOnHover &&
							"transition-transform duration-300 ease-[cubic-bezier(0.4,0,0.2,1)] group-hover/item:scale-105",
					)}
					aria-hidden={
						Boolean(item.href) && item.ariaLabel === undefined
					}
				>
					{item.node}
				</span>
			) : (
				<img
					className={cn(
						"block h-[var(--logoloop-logoHeight)] w-auto object-contain",
						"pointer-events-none [-webkit-user-drag:none]",
						"motion-reduce:transition-none",
						scaleOnHover &&
							"transition-transform duration-300 ease-[cubic-bezier(0.4,0,0.2,1)] group-hover/item:scale-105",
						logoImgClassName,
					)}
					src={item.src}
					srcSet={item.srcSet}
					sizes={item.sizes}
					width={item.width}
					height={item.height}
					alt={item.alt ?? ""}
					title={item.title}
					loading="lazy"
					decoding="async"
					draggable={false}
				/>
			);

			const itemAriaLabel = isNodeItem
				? (item.ariaLabel ?? item.title)
				: (item.alt ?? item.title);

			const inner = item.href ? (
				<a
					className={cn(
						"inline-flex items-center rounded no-underline",
						"transition-opacity duration-200 ease-linear",
						"hover:opacity-80",
						"focus-visible:outline focus-visible:outline-current focus-visible:outline-offset-2",
					)}
					href={item.href}
					aria-label={itemAriaLabel || "logo link"}
					target="_blank"
					rel="noreferrer noopener"
				>
					{content}
				</a>
			) : (
				content
			);

			const triggered = wrapWithTooltip(item, inner);

			return (
				<li
					className={cn(
						"flex-none text-[length:var(--logoloop-logoHeight)] leading-[1]",
						isVertical
							? "mb-[var(--logoloop-gap)]"
							: "mr-[var(--logoloop-gap)]",
						scaleOnHover && "group/item overflow-visible",
					)}
					key={key}
				>
					{triggered}
				</li>
			);
		},
		[isVertical, scaleOnHover, renderItem, logoImgClassName],
	);

	const logoLists = React.useMemo(
		() =>
			Array.from({ length: copyCount }, (_, copyIndex) => (
				<ul
					className={cn(
						"flex items-center",
						isVertical && "flex-col",
					)}
					key={`copy-${copyIndex}`}
					aria-hidden={copyIndex > 0}
					ref={copyIndex === 0 ? seqRef : undefined}
				>
					{logos.map((item, itemIndex) =>
						renderLogoItem(item, `${copyIndex}-${itemIndex}`),
					)}
				</ul>
			)),
		[copyCount, logos, renderLogoItem, isVertical],
	);

	const containerStyle = React.useMemo(
		(): CSSProperties => ({
			width: isVertical
				? toCssLength(width) === "100%"
					? undefined
					: toCssLength(width)
				: (toCssLength(width) ?? "100%"),
			...cssVariables,
			...style,
		}),
		[width, cssVariables, style, isVertical],
	);

	return (
		<section
			ref={containerRef}
			className={rootClasses}
			style={containerStyle}
			aria-label={ariaLabel}
			{...rest}
		>
			{fadeOut ? (
				isVertical ? (
					<>
						<div
							aria-hidden
							className={cn(
								"pointer-events-none absolute inset-x-0 top-0 z-10",
								"h-[clamp(24px,8%,120px)]",
								"bg-[linear-gradient(to_bottom,var(--logoloop-fadeColor,var(--logoloop-fadeColorAuto))_0%,rgba(0,0,0,0)_100%)]",
							)}
						/>
						<div
							aria-hidden
							className={cn(
								"pointer-events-none absolute inset-x-0 bottom-0 z-10",
								"h-[clamp(24px,8%,120px)]",
								"bg-[linear-gradient(to_top,var(--logoloop-fadeColor,var(--logoloop-fadeColorAuto))_0%,rgba(0,0,0,0)_100%)]",
							)}
						/>
					</>
				) : (
					<>
						<div
							aria-hidden
							className={cn(
								"pointer-events-none absolute inset-y-0 left-0 z-10",
								"w-[clamp(24px,8%,120px)]",
								"bg-[linear-gradient(to_right,var(--logoloop-fadeColor,var(--logoloop-fadeColorAuto))_0%,rgba(0,0,0,0)_100%)]",
							)}
						/>
						<div
							aria-hidden
							className={cn(
								"pointer-events-none absolute inset-y-0 right-0 z-10",
								"w-[clamp(24px,8%,120px)]",
								"bg-[linear-gradient(to_left,var(--logoloop-fadeColor,var(--logoloop-fadeColorAuto))_0%,rgba(0,0,0,0)_100%)]",
							)}
						/>
					</>
				)
			) : null}

			{/* biome-ignore lint/a11y/noStaticElementInteractions: Pointer enter/leave adjusts scroll velocity only; not a nested control. */}
			<div
				className={cn(
					"relative z-0 flex select-none will-change-transform",
					"motion-reduce:transform-none",
					isVertical ? "h-max w-full flex-col" : "w-max flex-row",
				)}
				ref={trackRef}
				onMouseEnter={handleMouseEnter}
				onMouseLeave={handleMouseLeave}
			>
				{logoLists}
			</div>
		</section>
	);
}

export const LogoLoop = React.memo(LogoLoopInner);
LogoLoop.displayName = "LogoLoop";

export default LogoLoop;
